import { useMemo, useState } from 'react';
import { Alert, Button, Input, Modal, Select, Textarea } from '@tutor/ui';
import { ApiError } from '../../lib/api-client';
import { useCreateLesson, useCurriculum, useStudents } from '../../hooks/queries';

export interface ScheduleLessonDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-selects the day when opened from a calendar cell. */
  defaultDate?: Date;
  onScheduled?: (lessonId: string) => void;
}

/** `datetime-local` needs a local-time string, not the ISO/UTC one `toISOString` gives. */
function toLocalInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function defaultStart(day?: Date): Date {
  const d = day ? new Date(day) : new Date();
  if (!day) d.setDate(d.getDate() + 1);
  // Next whole hour, so the picker never opens on an awkward time.
  d.setHours(day ? 16 : d.getHours() + 1, 0, 0, 0);
  return d;
}

export function ScheduleLessonDialog({
  open,
  onClose,
  defaultDate,
  onScheduled,
}: ScheduleLessonDialogProps) {
  const { data: students } = useStudents({ page: 1 });
  const { data: curriculum } = useCurriculum();
  const createLesson = useCreateLesson();

  const start = defaultStart(defaultDate);
  const end = new Date(start.getTime() + 60 * 60_000);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [studentIds, setStudentIds] = useState<string[]>([]);
  const [subjectId, setSubjectId] = useState('');
  const [topicId, setTopicId] = useState('');
  const [objectives, setObjectives] = useState('');
  const [startsAt, setStartsAt] = useState(toLocalInputValue(start));
  const [endsAt, setEndsAt] = useState(toLocalInputValue(end));
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);

  const topics = useMemo(() => {
    const subject = curriculum?.find((s) => s.id === subjectId);
    return subject?.courses.flatMap((course) => course.topics.map((t) => ({ ...t, course: course.name }))) ?? [];
  }, [curriculum, subjectId]);

  const roster = students?.items ?? [];

  const toggleStudent = (id: string): void => {
    setStudentIds((current) =>
      current.includes(id) ? current.filter((s) => s !== id) : [...current, id],
    );
  };

  // Keeping the duration when the start moves is what people expect from a scheduler.
  const onStartChange = (value: string): void => {
    const previousStart = new Date(startsAt).getTime();
    const previousEnd = new Date(endsAt).getTime();
    const duration = Number.isFinite(previousEnd - previousStart)
      ? previousEnd - previousStart
      : 60 * 60_000;

    setStartsAt(value);
    const next = new Date(value);
    if (!Number.isNaN(next.getTime())) setEndsAt(toLocalInputValue(new Date(next.getTime() + duration)));
  };

  const close = (): void => {
    setError(null);
    setConflict(null);
    onClose();
  };

  const onSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setConflict(null);

    if (studentIds.length === 0) {
      setError('Select at least one student.');
      return;
    }

    try {
      const lesson = await createLesson.mutateAsync({
        title: title.trim(),
        description: description.trim() || undefined,
        studentIds,
        subjectId: subjectId || undefined,
        topicId: topicId || undefined,
        objectives: objectives
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean),
        scheduledStart: new Date(startsAt).toISOString(),
        scheduledEnd: new Date(endsAt).toISOString(),
      });

      onScheduled?.(lesson.id);
      close();
    } catch (err) {
      if (err instanceof ApiError) {
        // A clash comes back as a 409 with the offending lessons in `details`.
        const clash = err.details?.scheduledStart?.[0];
        if (clash) setConflict(clash);
        else setError(err.message);
        return;
      }
      setError('We could not schedule that lesson.');
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Schedule a lesson"
      description="Only students on your roster can be added."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button type="submit" form="schedule-lesson-form" isLoading={createLesson.isPending}>
            Schedule lesson
          </Button>
        </>
      }
    >
      <form id="schedule-lesson-form" onSubmit={onSubmit} className="space-y-4" noValidate>
        {error && <Alert tone="danger">{error}</Alert>}
        {conflict && (
          <Alert tone="warning" title="That time is already taken">
            {conflict}
          </Alert>
        )}

        {roster.length === 0 && (
          <Alert tone="info" title="No students yet">
            Invite a student from the Students page before scheduling a lesson.
          </Alert>
        )}

        <Input
          label="Lesson title"
          required
          placeholder="Integration by parts"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />

        <div>
          <p className="mb-1.5 text-sm font-medium text-slate-700">Students</p>
          <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
            {roster.length === 0 ? (
              <p className="px-2 py-3 text-sm text-slate-500">Your roster is empty.</p>
            ) : (
              roster.map((student) => (
                <label
                  key={student.id}
                  className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    checked={studentIds.includes(student.id)}
                    onChange={() => toggleStudent(student.id)}
                  />
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-900">
                    {student.user.fullName}
                  </span>
                  <span className="truncate text-xs text-slate-400">
                    {student.subjects.map((s) => s.name).join(', ')}
                  </span>
                </label>
              ))
            )}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Starts"
            type="datetime-local"
            required
            value={startsAt}
            onChange={(event) => onStartChange(event.target.value)}
          />
          <Input
            label="Ends"
            type="datetime-local"
            required
            value={endsAt}
            onChange={(event) => setEndsAt(event.target.value)}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Subject"
            value={subjectId}
            onChange={(event) => {
              setSubjectId(event.target.value);
              setTopicId('');
            }}
          >
            <option value="">No subject</option>
            {curriculum?.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {subject.name}
              </option>
            ))}
          </Select>

          <Select
            label="Topic"
            disabled={!subjectId}
            value={topicId}
            onChange={(event) => setTopicId(event.target.value)}
          >
            <option value="">No topic</option>
            {topics.map((topic) => (
              <option key={topic.id} value={topic.id}>
                {topic.name}
              </option>
            ))}
          </Select>
        </div>

        <Textarea
          label="Description"
          hint="Optional."
          rows={2}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />

        <Textarea
          label="Objectives"
          hint="One per line. Optional."
          rows={3}
          placeholder={'Integrate by parts confidently\nChoose u and dv sensibly'}
          value={objectives}
          onChange={(event) => setObjectives(event.target.value)}
        />
      </form>
    </Modal>
  );
}
